import type { FeatureTranslation } from "../types";

export const keuanganEn: Record<string, FeatureTranslation> = {
  tagihan: {
    label: "Billing & Payments",
    navDescription: "Tuition, books, uniforms, school trips",
    summary:
      "Issue and collect every kind of school fee from one place, then take payment online.",
    detail: {
      title: "School bills that *collect themselves*, instead of being chased",
      lead: "Every fee type in one list — tuition, books, workbooks, uniforms, school trips, and institutional donations",
      intro:
        "The bursar issues a bill once, and the system does the chasing. Parents see the breakdown by fee type in the app, pay in part or in full, and the status on the school dashboard updates at that very moment. No more reconciling by hand in a spreadsheet.",
      highlights: [
        "Issue in bulk or import from a file, by class or by student",
        "Accurate status: unpaid, partially paid, and settled",
        "Parents pay one bill or all of them from the CARDS app",
      ],
      benefits: [
        {
          title: "Issue once, reaches every student",
          body: "Publish bills to a class, a year group, or the whole school. You can also import from a file when you have already prepared the figures yourself.",
        },
        {
          title: "Instalments stay tidy",
          body: "A partial payment does not break your books. The bill is marked “Partially Paid” and the remaining balance is always recalculated for you.",
        },
        {
          title: "Answer parents without digging",
          body: "Search by name, fee type, reference ID, or note. Filter by date and type to narrow a long list down to the one row you need.",
        },
        {
          title: "Reports without reconciling",
          body: "Payment reports build themselves from transactions already recorded, including cash payments logged at the school counter.",
        },
      ],
      screensTitle: "Two sides that stay in step",
      screensLead:
        "The bursar works in the school dashboard, parents see the result in the app — neither has to notify the other.",
      screens: [
        {
          alt: "Billing data page in the Cards School dashboard",
          caption: "Billing Data",
          note: "Every school bill with its status and filters",
        },
        {
          alt: "List of a student's bills in the CARDS parent app",
          caption: "Bills in the parent app",
          note: "Breakdown by type, pay one or pay all",
        },
        {
          alt: "Payment report with cash payment receipt",
          caption: "Payment Report",
          note: "Includes cash payments logged at the counter",
        },
      ],
      faq: [
        {
          q: "Can fee types match the terms our school already uses?",
          a: "Yes. You define the fee types yourself — tuition, meals, books, workbooks, uniforms, school trips, institutional donations, or whatever wording your school is used to.",
        },
        {
          q: "What happens when a parent pays only part of a bill?",
          a: "The bill is marked “Partially Paid” and the outstanding amount is calculated automatically. Parents can continue paying at any time until it is settled.",
        },
        {
          q: "What about parents who pay cash at the school counter?",
          a: "Staff record the cash payment in the dashboard and the receipt is stored against that bill, so your reports stay unified with online payments.",
        },
      ],
      metaTitle: "Online School Billing & Payment Software",
      metaDescription:
        "Manage tuition, book, uniform, and school trip fees in one system. Parents pay through the app while statuses and reports update on their own.",
      keywords: [
        "school billing software",
        "online tuition payment",
        "school fee management system",
        "school payment app",
        "student billing system",
      ],
    },
  },

  "cicilan-tunggakan": {
    label: "Instalments & Arrears",
    navDescription: "Partial payments & arrears reporting",
    summary:
      "See who is behind and by how much, and open up instalment options through FlexyCazh.",
    detail: {
      title: "Arrears you can see *early*, not once they have piled up",
      lead: "Staged payments are recorded properly, and every student's outstanding balance stays current",
      intro:
        "Not every family can settle in one go, and that is no reason for your books to fall apart. Each partial payment reduces the balance immediately, the status changes on its own, and the arrears list assembles itself without anyone recalculating it each month.",
      highlights: [
        "Outstanding balances recalculated with every payment received",
        "An arrears list that builds itself, ready to act on",
        "Interest-free instalments through FlexyCazh for families who need them",
      ],
      benefits: [
        {
          title: "Any amount, still recorded correctly",
          body: "Parents can pay what they are able to. The system marks the bill as partially paid and keeps the remainder, rather than rejecting a payment that is not exact.",
        },
        {
          title: "Know who needs a conversation",
          body: "The arrears list shows the student, the fee type, and the amount outstanding — a clear basis before a form teacher picks up the phone.",
        },
        {
          title: "A history you can show",
          body: "Every payment carries its own time, amount, and status. When a parent remembers things differently, you can open the record together.",
        },
        {
          title: "Formal instalments via FlexyCazh",
          body: "For larger burdens such as building fees, FlexyCazh lets families pay in interest-free instalments while the school still receives the full amount.",
        },
      ],
      screensTitle: "What paying in stages looks like",
      screensLead:
        "The payment flow and its history are deliberately open, so no one has to ask where the money went.",
      screens: [
        {
          alt: "Paying a bill in the CARDS app",
          caption: "Payment",
          note: "Pay in part or in full, with a clear breakdown",
        },
        {
          alt: "Detailed history of a successful bill payment",
          caption: "Payment History",
          note: "Proof of every payment, with its status",
        },
      ],
      faq: [
        {
          q: "Can the school set a minimum instalment amount?",
          a: "Instalment policy follows your school's rules. You decide which fee types may be paid in stages and on what terms; the system keeps track of the balance.",
        },
        {
          q: "How is a normal instalment different from FlexyCazh?",
          a: "A normal instalment is a staged payment made directly to the school. FlexyCazh is an interest-free instalment scheme for parents, while the school still receives payment in full.",
        },
        {
          q: "Can arrears reports be pulled per class?",
          a: "Yes. The arrears list can be filtered and searched, so a form teacher only sees the students in their own class.",
        },
      ],
      metaTitle: "Manage School Fee Instalments & Arrears",
      metaDescription:
        "Staged payments recorded automatically with balances always current. Arrears lists build themselves, with interest-free instalments via FlexyCazh.",
      keywords: [
        "school arrears software",
        "tuition instalment plan",
        "school fee arrears report",
        "staged school payments",
        "school receivables management",
      ],
    },
  },

  tabungan: {
    label: "School Savings",
    navDescription: "Savings products & student accounts",
    summary:
      "Run savings products and student accounts with deposits and withdrawals that are recorded and visible to parents.",
    detail: {
      title: "Student savings run *like a bank*, not a paper passbook",
      lead: "Savings products, an account per member, and deposits and withdrawals recorded at every step",
      intro:
        "The school defines its own savings products, then opens accounts for students, teachers, and staff — one at a time or in bulk. Every deposit and withdrawal is recorded with its transaction type, timestamp, and PIN confirmation, so each balance can always be accounted for.",
      highlights: [
        "Several savings products at once, matching your programmes",
        "Open accounts in bulk for students, teachers, or staff",
        "Cash and wallet transactions, each confirmed by PIN",
      ],
      benefits: [
        {
          title: "Products that follow your programmes",
          body: "Create more than one savings product with its own terms, then filter the account list by product or by institution.",
        },
        {
          title: "Open accounts without doing it one by one",
          body: "Bulk account creation is available for students, teachers, and staff — far quicker than registering each person individually at the start of the year.",
        },
        {
          title: "Every transaction is confirmed",
          body: "Deposits and withdrawals, whether cash or from a wallet, pass through PIN confirmation. A wrong PIN is rejected, so nothing goes through unapproved.",
        },
        {
          title: "Accounts you can control",
          body: "Accounts can be blocked, deactivated, reactivated, and have cards printed — all from the same account detail page.",
        },
      ],
      screensTitle: "From product to account statement",
      screensLead:
        "Savings staff work from two pages: the account list for the overall picture, the account detail for daily transactions.",
      screens: [
        {
          alt: "Savings accounts by product in the Cards School dashboard",
          caption: "Accounts by Product",
          note: "Filter by product, institution, and account status",
        },
        {
          alt: "Student savings account detail with transaction history",
          caption: "Account Detail",
          note: "Statement, new transaction, block, and card printing",
        },
      ],
      faq: [
        {
          q: "Is this separate from the balance on the student card?",
          a: "Yes, the two are separate. The card balance is for daily spending such as the canteen, while savings is a deposit with its own product and terms.",
        },
        {
          q: "Can parents see their child's savings balance?",
          a: "Yes. School savings appear in the parent app, so the balance and statement are visible without having to ask a member of staff.",
        },
        {
          q: "What happens when a student transfers or graduates?",
          a: "The account can be deactivated or blocked before the balance is settled, and its status is recorded so it is never confused with active accounts.",
        },
      ],
      metaTitle: "Student Savings & School Account Software",
      metaDescription:
        "Run savings products and accounts for students, teachers, and staff. PIN-confirmed deposits and withdrawals, recorded statements, balances open to parents.",
      keywords: [
        "student savings software",
        "school savings account system",
        "digital school savings",
        "student deposit management",
        "school banking software",
      ],
    },
  },

  "tabungan-cita": {
    label: "Goal Savings",
    navDescription: "Goal-based, fixed-term savings",
    summary:
      "Savings with a goal and a timeframe — pilgrimage, graduation, or a school trip — with progress you can see.",
    detail: {
      title: "Saving toward *a goal you can name*, not just setting money aside",
      lead: "Fixed-term savings with a target and a duration, so parents know what they are working toward",
      intro:
        "Large costs such as a pilgrimage, graduation, or a school trip feel far lighter when spread over months. Goal Savings gives each objective its own target amount and duration, then shows the progress in the parent app — so saving has a direction rather than being money simply left aside.",
      highlights: [
        "One goal, one target amount, and its own savings duration",
        "Progress visible right on the parent app home screen",
        "Runs alongside ordinary school savings",
      ],
      benefits: [
        {
          title: "A goal with a name",
          body: "Not just a balance. Each savings plan carries the name of its objective and its target amount, so parents know exactly what the money is for.",
        },
        {
          title: "A duration that makes the plan real",
          body: "Every goal has a timeframe, say twelve months. From that, parents can work out roughly how much to set aside each month.",
        },
        {
          title: "Big costs broken up",
          body: "An amount that is heavy in one payment becomes far lighter when gathered from the start of the year, and the school never has to collect it at short notice.",
        },
        {
          title: "Visible every time the app opens",
          body: "Progress sits on the home screen rather than buried in a submenu — a gentle reminder that keeps the plan from being forgotten.",
        },
      ],
      screensTitle: "Savings goals on the parent home screen",
      screensLead:
        "Placed alongside balances and bills, so progress reads at a glance.",
      screens: [
        {
          alt: "Goal Savings card showing target and duration in the CARDS app",
          caption: "Goal Savings",
          note: "Target amount and duration for each objective",
        },
      ],
      faq: [
        {
          q: "Can the school decide which savings goals are offered?",
          a: "Yes. The school decides which goals are opened — pilgrimage, graduation, school trips, or other programmes — along with their target amounts and durations.",
        },
        {
          q: "Can Goal Savings be withdrawn at any time?",
          a: "Withdrawal terms follow your school's policy for that savings product, since Goal Savings is designed as a fixed-term deposit working toward a single objective.",
        },
        {
          q: "Can one student have more than one goal?",
          a: "Yes. Each goal stands on its own with its own target and duration, and they all appear in the parent app.",
        },
      ],
      metaTitle: "Goal Savings — Fixed-Term Student Savings",
      metaDescription:
        "School savings with a goal and a timeframe, such as a pilgrimage or graduation. Targets and progress appear directly in the parent app.",
      keywords: [
        "goal based student savings",
        "fixed term school savings",
        "school savings programme",
        "graduation savings plan",
        "student savings goals",
      ],
    },
  },

  pembukuan: {
    label: "Bookkeeping & Reports",
    navDescription: "Journals, P&L, financial position",
    summary:
      "Journals, journal history, statements of financial position, profit and loss, and revenue sharing in one set of books.",
    detail: {
      title: "Books that *grow from transactions*, not from retyping",
      lead: "Journals, statements of financial position, profit and loss, and revenue sharing from data already in the system",
      intro:
        "Bills, savings, and card transactions are already recorded in CARDS. Bookkeeping draws from there rather than asking the bursar to enter everything a second time into separate software. Income and expenses outside those automatic transactions are entered through manual journals, changes are kept, and reports can be pulled whenever the foundation asks.",
      highlights: [
        "Automatic and manual journals, all recorded with an auditable history",
        "Four standard reports: chart of accounts, balance sheet, profit & loss, and cash flow — per ISAK 35",
        "Revenue sharing calculations and account migration when switching systems",
      ],
      benefits: [
        {
          title: "One source of data, not two",
          body: "Transactions already captured through billing, savings, and cards become the raw material for your books. Anything outside them — any income or expense — is entered through a manual journal, in the same books, with no double entry to cause discrepancies.",
        },
        {
          title: "The reports your foundation asks for",
          body: "The chart of accounts, balance sheet, profit & loss, and cash flow statement — all per ISAK 35 — are available immediately, so a request before a board meeting no longer means a late night assembling figures.",
        },
        {
          title: "Changes leave a trail",
          body: "Every transaction is recorded, and journal history keeps what changed — corrections remain traceable and the books stay auditable.",
        },
        {
          title: "Switch systems without starting over",
          body: "Account migration lets you bring across the chart of accounts you already use, rather than rebuilding it from nothing.",
        },
      ],
      screensTitle: "From chart of accounts to cash flow",
      screensLead:
        "Four standard reports — chart of accounts, balance sheet, profit & loss, and cash flow — pulled straight from the books, following Cazh templates per ISAK 35.",
      screens: [
        {
          alt: "General Reports page offering COA, balance sheet, profit and loss, and cash flow reports",
          caption: "General Reports",
          note: "Four ready-made reports: chart of accounts, balance sheet, profit & loss, and cash flow",
        },
        {
          alt: "Chart of accounts report listing accounts per sub-group with opening balance, movements, and closing balance",
          caption: "Chart of Accounts",
          note: "Accounts per sub-group with opening balance, debits, credits, and closing balance",
        },
        {
          alt: "Balance sheet — ISAK 35 statement of financial position with current and non-current assets",
          caption: "Balance Sheet",
          note: "ISAK 35 statement of financial position — pick the office and period, the report assembles",
        },
        {
          alt: "ISAK 35 indirect-method cash flow statement with operating and investing activities",
          caption: "Cash Flow",
          note: "ISAK 35 cash flow statement (indirect method), from operating through investing activities",
        },
        {
          alt: "Manual journal form with debit account, credit account, amount, and a proof-of-transaction upload",
          caption: "Manual Journal",
          note: "Income or expenses outside automatic transactions are entered manually, proof attached",
        },
        {
          alt: "Bottom of the balance sheet showing a zero difference labelled Balanced",
          caption: "A balance sheet that checks itself",
          note: "Total assets minus liabilities & net assets must equal 0 — labelled Balanced",
        },
      ],
      faq: [
        {
          q: "How do we record income or expenses that don't run through CARDS?",
          a: "Through the Manual Journal: pick the debit and credit accounts, enter the amount and a note, and attach the proof of transaction. Manual entries land in the same journal with their own history, so they stay auditable.",
        },
        {
          q: "Does this replace our accounting software?",
          a: "CARDS bookkeeping covers journals, statements of financial position, profit and loss, and revenue sharing for transactions running through CARDS. For broader foundation accounting, our team can walk through the scope with you.",
        },
        {
          q: "Can we keep our existing chart of accounts?",
          a: "Yes. Account migration is available, so the accounts you already use can be brought into the system rather than rebuilt.",
        },
        {
          q: "Who can open the financial reports?",
          a: "Access follows the user settings in your dashboard, so financial reports are only visible to the roles you grant permission to.",
        },
      ],
      metaTitle: "School Bookkeeping & Financial Reporting",
      metaDescription:
        "Journals, statements of financial position, profit and loss, and revenue sharing built from billing, savings, and card transactions — with no re-entry.",
      keywords: [
        "school bookkeeping software",
        "school financial reports",
        "school accounting system",
        "school general ledger",
        "school profit and loss report",
      ],
    },
  },

  donasi: {
    label: "Donations",
    navDescription: "Transparent fundraising",
    summary:
      "Run school fundraising with records and reporting you can stand behind.",
    detail: {
      title: "Fundraising you can *actually account for*",
      lead: "Donations arrive through the same channel as every other payment, and are recorded from the first rupiah",
      intro:
        "Building a prayer hall, supporting families in hardship, or responding to a sudden emergency — fundraising often runs through a personal bank account and a handwritten list. The donations module moves it onto the school's official channel, so every contribution is recorded and the report can be shown to parents.",
      highlights: [
        "Donations arrive through the school's official payment channel",
        "Recorded automatically, not reconciled by hand from bank statements",
        "Flows straight into the school's books",
      ],
      benefits: [
        {
          title: "The same door as every other payment",
          body: "Parents donate from the app they already use to pay bills, with no manual transfer to a separate account.",
        },
        {
          title: "A complete record from the start",
          body: "Every donation is stored with its amount and timestamp, so nothing slips through because someone forgot to write it down.",
        },
        {
          title: "Reporting that keeps trust intact",
          body: "Because contributions are recorded systematically, the report on how funds were used comes from the same data — not from memory or a pile of transfer receipts.",
        },
        {
          title: "Part of your books",
          body: "Donations received flow into the school's bookkeeping, so your financial position stays whole without a separate ledger.",
        },
      ],
      screensTitle: "From intention to report",
      screensLead:
        "Four steps that stop fundraising from depending on someone's private notes.",
      steps: [
        {
          title: "The school opens a campaign",
          body: "Define the programme — construction, scholarships, or emergency relief — along with the details parents need to know.",
        },
        {
          title: "Parents donate",
          body: "Donations are made from the CARDS app, the same channel used for paying bills, with no manual transfer.",
        },
        {
          title: "Contributions are recorded",
          body: "Every donation is stored with its amount and timestamp, so the running total is always current.",
        },
        {
          title: "The report is drawn up",
          body: "From that same record, the school prepares a statement of funds received and used to share back with parents.",
        },
      ],
      faq: [
        {
          q: "Can donors give without their name being shown?",
          a: "Whether names are displayed follows your school's policy for that campaign. Either way, every contribution is still recorded in the system.",
        },
        {
          q: "Can more than one campaign run at a time?",
          a: "Yes. Campaigns can run in parallel as your school needs, with contributions recorded against each one.",
        },
        {
          q: "Do donations flow into the financial reports automatically?",
          a: "Yes. Donations received become part of the school's bookkeeping, so there is no need to record them separately.",
        },
      ],
      metaTitle: "Transparent Online School Donations",
      metaDescription:
        "Run school fundraising through your official payment channel. Every donation is recorded automatically and flows into your books, ready to report to parents.",
      keywords: [
        "online school donations",
        "school fundraising software",
        "school donation platform",
        "transparent school fundraising",
        "school donation reporting",
      ],
    },
  },
  inventaris: {
    label: "Inventory",
    navDescription: "Institution assets & goods",
    summary:
      "Record the school's assets and goods in one orderly list, instead of a stray exercise book.",
    detail: {
      title: "School assets that are *on record*, not just remembered",
      lead: "An inventory of the institution's goods in one list, with its own configuration",
      intro:
        "Desks, computers, teaching aids, attendance devices — a school's belongings keep growing, and the records often stay in one caretaker's notebook. The inventory module moves them into the same system as the institution's data, so the asset list can always be opened and accounted for.",
      highlights: [
        "Every asset and item of the institution recorded in one list",
        "Category settings that follow how your institution works",
        "Lives in the same system as the school's data and finances",
      ],
      benefits: [
        {
          title: "No longer dependent on one caretaker",
          body: "Asset records live in the system, not a private notebook. A change of facilities staff no longer takes the history of the goods with it.",
        },
        {
          title: "Handovers with a clear basis",
          body: "An orderly list means handovers between administrators, or a foundation audit, start from data rather than a full recount of the storeroom.",
        },
        {
          title: "Categories you define",
          body: "Inventory settings let you group assets the way your institution sees them — by room, by type, or by the person responsible.",
        },
        {
          title: "One system with the finances",
          body: "Because it sits inside CARDS, the asset record lives alongside the bookkeeping — helping the foundation see the institution's holdings as a whole.",
        },
      ],
      screensTitle: "The inventory flow",
      screensLead:
        "It starts with category setup, then recording simply follows the goods as they arrive.",
      steps: [
        {
          title: "Set up the categories",
          body: "Define the asset groupings through inventory settings, matching how your institution organises its goods.",
        },
        {
          title: "Record the items",
          body: "Every asset and item belonging to the institution is entered into the inventory list with its details.",
        },
        {
          title: "Manage and keep it current",
          body: "Changes in an item's condition or whereabouts are updated on the same list, keeping the record alive.",
        },
        {
          title: "Open it when it matters",
          body: "At handovers, audits, or procurement planning, the asset list is ready to open without a recount.",
        },
      ],
      faq: [
        {
          q: "What kinds of items can be recorded?",
          a: "Any asset or item the institution owns — from classroom furniture to electronic devices. The groupings are yours to define through inventory settings.",
        },
        {
          q: "Who is allowed to manage the inventory?",
          a: "Access follows the user settings in your dashboard, so only the roles you authorise can add or change asset records.",
        },
        {
          q: "Is the inventory connected to the bookkeeping?",
          a: "Both live in the same system, so the asset record can be read alongside the institution's financial reports.",
        },
      ],
      metaTitle: "School & Pesantren Asset Inventory Software",
      metaDescription:
        "Record your school's assets and goods in one orderly list — with configurable categories, living in the same system as the institution's finances.",
      keywords: [
        "school inventory software",
        "school asset management",
        "school asset records",
        "pesantren asset management",
        "school facilities inventory",
      ],
    },
  },
};
